import { FC, memo, useEffect, useState } from "react";
import { Button, List, message, UploadFile } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { DownloadOutlined, InboxOutlined } from "@ant-design/icons";
import { MapData_Base, MapData_InputFile } from "~/common/typescript/models/MapData";
import { MapDataAPIs } from "~/common/typescript/apis/MapDataAPIs";
import FormItemWrapper from "~/components/common/FormItemWrapper/FormItemWrapper";
import styled from "styled-components";

interface MapData_UploadFile_Props {
  mapData: MapData_Base;
}

const MapData_UploadFile: FC<MapData_UploadFile_Props> = ({ mapData }) => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [inputFiles, setInputFiles] = useState<MapData_InputFile[]>([]);

  useEffect(() => {
    if (mapData) {
      MapDataAPIs.remote_mapData_getInputFiles(mapData.id!).then(setInputFiles);
    }
  }, [mapData]);

  const handleUpload = async () => {
    if (!mapData?.id || files.length === 0) return;

    setUploading(true);
    try {
      await MapDataAPIs.remote_mapData_upload(mapData.id, files.map(f => f.originFileObj as File));
      message.success('Tải lên thành công!');
      setFiles([]);
      // Refresh input files
      MapDataAPIs.remote_mapData_getInputFiles(mapData.id).then(setInputFiles);
    } catch (error) {
      console.error('Upload failed', error);
      message.error('Tải lên thất bại!');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (file: MapData_InputFile) => {
    if (!mapData?.id) return;
    setDownloading(file.name);
    try {
      const blob = await MapDataAPIs.remote_mapData_downloadInputFile(mapData.id, file.name);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed', error);
      message.error('Tải về thất bại!');
    } finally {
      setDownloading(null);
    }
  }

  return (
    <UI_Wrapper>
      <FormItemWrapper label="Các tệp đã tải lên" labelWidth={300}>
        <List
          bordered
          dataSource={inputFiles}
          renderItem={item => (
            <List.Item
              actions={[<Button icon={<DownloadOutlined />} loading={downloading === item.name} onClick={() => handleDownload(item)} />]}
            >
              {item.name}
            </List.Item>
          )}
        />
      </FormItemWrapper>
      <FormItemWrapper label="Tải lên tệp mới" labelWidth={300}>
        <div>
          <Dragger
            multiple
            fileList={files}
            onChange={(info) => setFiles(info.fileList)}
            beforeUpload={() => false}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Kéo thả hoặc chọn tệp</p>
          </Dragger>
          <UI_UploadButtonWrapper>
            <Button onClick={handleUpload} loading={uploading}>
              Tải lên
            </Button>
          </UI_UploadButtonWrapper>
        </div>
      </FormItemWrapper>
    </UI_Wrapper>
  )
}

export default memo(MapData_UploadFile);

const UI_Wrapper = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 16px;
`;

const UI_UploadButtonWrapper = styled.div`
  text-align: right;
  margin-top: 8px;
`;